import React, { RefObject, useMemo, useRef, useState } from 'react'
import { Col, Input as InputAntd, Row } from 'antd'
import { SelectValue } from 'antd/lib/select'
import { FormikProps } from 'formik'

import { Id, RequestItinerary } from 'src/types'

import {
  DatePickerRange,
  Input,
  Select,
  SelectResource
} from 'src/components/Formik'

interface Place {
  label: string
  value: string
}

interface Prediction {
  description: string
  place_id: string
}

type Props = {
  formikProps: FormikProps<RequestItinerary>
  organization_id: Id
  refActivityInput: RefObject<InputAntd>
}

function FormFields({
  formikProps,
  organization_id,
  refActivityInput
}: Props): JSX.Element {
  const googleAutocompleteService = useMemo(() => {
    // @ts-ignore
    return new window.google.maps.places.AutocompleteService()
  }, [])

  const googleGeocoderService = useMemo(() => {
    // @ts-ignore
    return new window.google.maps.Geocoder()
  }, [])

  const refTimeout = useRef<NodeJS.Timeout>(null)

  const [loadingPlacesList, setLoadingPlacesList] = useState(false)
  const [placesList, setPlacesList] = useState<Place[]>([])

  return (
    <>
      <Row gutter={24}>
        <Col span={12}>
          <Input
            autoFocus
            data-test-id="itinerary-activity"
            formikProps={formikProps}
            innerRef={refActivityInput}
            label="web.itinerary.form.activity.title"
            name="activity"
            placeholder="web.itinerary.form.activity.placeholder"
          />
        </Col>
        <Col span={8}>
          <SelectResource
            formikProps={formikProps}
            keyLabel="name"
            keyValue="reference_id"
            label="web.itinerary.form.reference.title"
            name="reference_id"
            organization_id={organization_id}
            placeholder="web.itinerary.form.reference.placeholder"
            resource="references"
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12}>
          <Select
            dataTestId="place-list"
            filterOption={false}
            formikProps={formikProps}
            label="web.itinerary.form.place.title"
            loading={loadingPlacesList}
            name="description"
            onChange={(
              value: SelectValue,
              option: React.ReactElement<any> | React.ReactElement<any>[]
            ) => {
              const place: Place = {
                // @ts-ignore
                label: option.props.title,
                // @ts-ignore
                value: option.props.value
              }
              formikProps.setFieldValue('description', place.label)
              googleGeocoderService.geocode(
                { placeId: place.value },
                (results: any) => {
                  formikProps.setFieldValue(
                    'latitude',
                    results[0].geometry.location.lat()
                  )
                  formikProps.setFieldValue(
                    'longitude',
                    results[0].geometry.location.lng()
                  )
                }
              )
            }}
            onSearch={(value: string) => {
              clearTimeout(refTimeout.current)
              refTimeout.current = setTimeout(() => {
                if (value) {
                  setLoadingPlacesList(true)
                  googleAutocompleteService.getPlacePredictions(
                    { input: value },
                    (predictions: any) => {
                      if (predictions) {
                        setPlacesList(
                          predictions.map(
                            ({ description, place_id }: Prediction) => ({
                              label: description,
                              value: place_id
                            })
                          )
                        )
                      }
                      setLoadingPlacesList(false)
                    }
                  )
                }
              }, 500)
            }}
            options={placesList}
            placeholder="web.itinerary.form.place.placeholder"
            showSearch
          />
        </Col>
        <Col span={12}>
          <DatePickerRange
            formikProps={formikProps}
            label="web.itinerary.form.period.placeholder"
            name={['started_on', 'ended_on']}
            placeholder={[
              'web.itinerary.form.period.start-time',
              'web.itinerary.form.period.end-time'
            ]}
          />
        </Col>
      </Row>
    </>
  )
}

export default FormFields
